import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MagnifyingGlass, Bell, SignOut, User as UserIcon, Sun, Moon, Gear, Check, CheckCircle,
  Lifebuoy, Plus, ClockCounterClockwise, Question, X as XIcon, FileText,
} from "@phosphor-icons/react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useBrand } from "./AppShell";
import UserProfileDrawer from "./SelfProfileDrawer";

const SEARCH_LABELS = {
  cases: "Cases",
  jobs: "Jobs",
  subjects: "Subjects",
  locations: "Locations",
  customers: "Customers",
};
const SEARCH_ROUTES = {
  cases: "/cases",
  jobs: "/jobs",
  subjects: "/subjects",
  locations: "/locations",
  customers: "/customers",
};

const NOTIF_GROUP_LABELS = {
  messages: "Messages",
  jobs: "Jobs",
  tasks: "Tasks",
  emails: "Emails",
  other: "Other",
};

export default function TopBar() {
  const { user, logout, setUser } = useAuth();
  const { brand } = useBrand();
  const navigate = useNavigate();

  // Global keyboard shortcut: ⌘J / Ctrl+J / ⌘. / Ctrl+. → open the New Job drawer.
  useEffect(() => {
    const onKey = (e) => {
      // Don't hijack the shortcut while the user is typing in a field.
      const tag = (e.target?.tagName || "").toLowerCase();
      const editable = tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable;
      if (editable) return;
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (e.key === "j" || e.key === "J" || e.key === ".")) {
        e.preventDefault();
        navigate("/jobs?new=1");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  // --- search state -------------------------------------------------------
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchRes, setSearchRes] = useState(null);
  const [searching, setSearching] = useState(false);
  const searchBoxRef = useRef(null);
  const searchInputRef = useRef(null);

  // Cmd/Ctrl + K focuses the search input from anywhere in the app.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
        setSearchOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!q || q.trim().length < 2) { setSearchRes(null); return undefined; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
        setSearchRes(r.data);
      } catch { setSearchRes(null); }
      finally { setSearching(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // --- notifications state ------------------------------------------------
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifData, setNotifData] = useState(null);
  const notifBtnRef = useRef(null);

  const loadNotif = async () => {
    try {
      const r = await api.get("/notifications?limit=40");
      setNotifData(r.data);
    } catch { setNotifData({ items: [], counts: { total_unread: 0 } }); }
  };

  useEffect(() => {
    loadNotif();
    const t = setInterval(loadNotif, 60000); // refresh every 60s
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (notifBtnRef.current && !notifBtnRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // --- avatar dropdown state ----------------------------------------------
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const toggleTheme = async () => {
    const next = user?.theme === "dark" ? "light" : "dark";
    try {
      const r = await api.patch("/users/me", { theme: next });
      setUser(r.data);
    } catch (e) { console.warn(formatErr(e)); }
  };

  const onPickSearchResult = (kind, id) => {
    setSearchOpen(false);
    setQ("");
    navigate(`${SEARCH_ROUTES[kind] || "/"}?highlight=${id}`);
  };

  const onPickNotification = async (n) => {
    setNotifOpen(false);
    try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    if (n.job_id) navigate(`/jobs?highlight=${n.job_id}`);
    else if (n.case_id) navigate(`/cases?highlight=${n.case_id}`);
    else if (n.email_intake_id) navigate(`/intake?highlight=${n.email_intake_id}`);
    loadNotif();
  };

  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
    loadNotif();
  };

  const groupedNotifs = useMemo(() => {
    if (!notifData?.items) return {};
    const acc = {};
    for (const n of notifData.items) {
      const g = NOTIF_GROUP_LABELS[n.group] ? n.group : "other";
      (acc[g] = acc[g] || []).push(n);
    }
    return acc;
  }, [notifData]);

  const totalUnread = notifData?.counts?.total_unread || 0;
  const initial = (user?.first_name || user?.name || "?").trim()[0]?.toUpperCase() || "?";

  return (
    <header
      className="topbar h-12 border-b border-border bg-card text-foreground grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-4 sticky top-0 z-[70]"
      data-testid="top-bar"
    >
      {/* LEFT: org brand — left-aligned. `flex-shrink-0` so the org name never
          gets squeezed by the flexible center column.
          iter 66: now reads from BrandProvider (useBrand) so the org's
          uploaded logo overrides the default. Falls back to the cached
          `user.org_logo_url` while /api/brand is still in flight, then to
          plain text + a generic glyph if no logo at all. */}
      <div className="flex items-center justify-start min-w-0 flex-shrink-0">
        {(brand?.logo_url || user?.org_logo_url) ? (
          <img
            src={brand?.logo_url || user.org_logo_url}
            alt={brand?.org_name || user?.org_name || "Org"}
            className="h-7 max-w-[140px] object-contain"
            data-testid="topbar-org-logo"
          />
        ) : (
          <span
            className="text-sm font-semibold truncate max-w-[200px]"
            title={brand?.org_name || user?.org_name || ""}
            data-testid="topbar-org-name"
          >
            {brand?.org_name || user?.org_name || ""}
          </span>
        )}
      </div>

      {/* CENTER: NEW JOB button + search — true-centered, max width preserved.
          iter 44 follow-up: NEW JOB moved here (was in the right cluster and
          overlapping the search bar on narrower viewports). It now sits
          immediately to the LEFT of the search box. */}
      <div className="flex justify-center">
        <div className="flex items-center gap-2 w-full max-w-4xl" ref={searchBoxRef}>
          <button
            onClick={() => navigate("/jobs?new=1")}
            className="btn btn-primary btn-sm flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
            data-testid="topbar-new-job-btn"
            title="Create a new job (⌘J)"
          >
            <Plus size={14} /> NEW JOB
            <kbd
              className="hidden md:inline-flex items-center text-[10px] mono border border-white/30 rounded px-1 py-0.5 ml-1 select-none"
              data-testid="topbar-new-job-shortcut"
              title="Also: ⌘. / Ctrl+."
            >
              ⌘J
            </kbd>
          </button>
          <div className="relative flex-1 min-w-0">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input
                ref={searchInputRef}
                className="input pr-20 h-9"
                style={{ paddingLeft: "2.5rem" }}
                placeholder="Search cases, jobs, subjects, locations, customers…"
                value={q}
                onChange={(e) => { setQ(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)}
                data-testid="topbar-search-input"
            />
            <kbd
              className="hidden md:flex absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] mono text-muted-foreground border border-border rounded px-1.5 py-0.5 select-none pointer-events-none bg-muted/40"
              data-testid="topbar-search-shortcut"
            >
              ⌘K
            </kbd>
          </div>
          {searchOpen && q.trim().length >= 2 && (
            <div
              className="absolute left-0 right-0 mt-1 surface shadow-lg max-h-[60vh] overflow-y-auto"
              data-testid="topbar-search-results"
            >
              {searching && <div className="p-3 text-xs text-muted-foreground">Searching…</div>}
              {!searching && searchRes?.total === 0 && (
                <div className="p-4 text-xs text-muted-foreground" data-testid="search-empty">No results.</div>
              )}
              {!searching && searchRes?.groups && Object.entries(searchRes.groups).map(([kind, rows]) =>
                rows.length === 0 ? null : (
                  <div key={kind} data-testid={`search-group-${kind}`}>
                    <div className="px-3 pt-2 pb-1 label-xs">{SEARCH_LABELS[kind] || kind}</div>
                    {rows.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => onPickSearchResult(kind, r.id)}
                        className="w-full text-left px-3 py-2 hover:bg-muted/40 border-b border-border last:border-0"
                        data-testid={`search-result-${kind}-${r.id}`}
                      >
                        <div className="text-sm truncate">{r.title}</div>
                        {r.subtitle && <div className="text-[11px] text-muted-foreground truncate mono">{r.subtitle}</div>}
                      </button>
                    ))}
                  </div>
                ),
              )}
            </div>
          )}
          </div>{/* /relative flex-1 min-w-0 wrapping the input + dropdown */}
        </div>
      </div>

      {/* RIGHT: support · templates · settings · help · bell · avatar — right-aligned.
          `flex-shrink-0` on the cluster ensures it never gets squeezed by the
          flexible center column (search bar will shrink instead). */}
      <div className="flex items-center justify-end gap-2 flex-shrink-0">
        {/* iter 44 follow-up — NEW JOB button removed from this cluster and
            moved into the CENTER cell next to the search input (was overlapping
            the search bar on narrower viewports). */}
        {/* Support — labeled icon so users immediately understand the action. */}
        <button
          onClick={() => navigate("/support")}
          className="btn btn-ghost btn-sm flex items-center gap-1.5"
          data-testid="topbar-support-btn"
          aria-label="Support"
          title="Customer support"
        >
          <Lifebuoy size={16} />
          <span className="hidden sm:inline text-xs font-semibold tracking-wide">SUPPORT</span>
        </button>

        {/* iter 44 — Templates: bold-bordered TopBar button, replaces the
            sidebar entry. The bolder border distinguishes it from the
            ghost-style support button. */}
        <button
          onClick={() => navigate("/templates")}
          className="btn btn-sm flex items-center gap-1.5"
          style={{ border: "2px solid hsl(var(--foreground))", fontWeight: 700 }}
          data-testid="topbar-templates-btn"
          aria-label="Templates"
          title="Templates"
        >
          <FileText size={14} />
          <span className="text-xs tracking-wide">TEMPLATES</span>
        </button>

        {/* iter 44 — Settings gear in main TopBar row. The ONLY entry-point to
            /settings (we removed it from the sidebar and the avatar mini-menu
            as part of this iter). */}
        <button
          onClick={() => navigate("/settings")}
          className="btn btn-ghost btn-sm flex items-center gap-1.5"
          data-testid="topbar-settings-btn"
          aria-label="Settings"
          title="Settings"
        >
          <Gear size={16} />
          <span className="hidden sm:inline text-xs font-semibold tracking-wide">SETTINGS</span>
        </button>

        {/* Recently-viewed jobs (iter 39). Server-persisted via /users/me/recent-jobs. */}
        <RecentJobsMenu />

        {/* Keyboard shortcuts cheatsheet (iter 39). */}
        <ShortcutsMenu />

        {/* Bell */}
        <div className="relative" ref={notifBtnRef}>
          <button
            onClick={() => setNotifOpen((o) => !o)}
            className="btn btn-ghost btn-sm relative"
            data-testid="topbar-bell-btn"
            aria-label="Notifications"
            title="Notifications"
          >
            <Bell size={16} />
            {totalUnread > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 bg-destructive text-destructive-foreground text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
                data-testid="topbar-bell-badge"
              >
                {totalUnread > 99 ? "99+" : totalUnread}
              </span>
            )}
          </button>
          {notifOpen && (
            <div className="absolute right-0 mt-1 w-96 surface shadow-lg max-h-[70vh] overflow-y-auto" data-testid="topbar-notif-panel">
              <div className="flex items-center justify-between p-3 border-b border-border sticky top-0 bg-card">
                <span className="label-xs">Notifications · {totalUnread} unread</span>
                <button onClick={markAllRead} className="btn btn-ghost btn-sm" data-testid="notif-mark-all-read">
                  <CheckCircle size={12} /> READ ALL
                </button>
              </div>
              {(!notifData || notifData.items.length === 0) && (
                <div className="p-4 text-xs text-muted-foreground" data-testid="notif-empty">No notifications.</div>
              )}
              {Object.entries(groupedNotifs).map(([g, items]) => (
                <div key={g} data-testid={`notif-group-${g}`}>
                  <div className="px-3 pt-2 pb-1 label-xs sticky top-12 bg-card">{NOTIF_GROUP_LABELS[g]}</div>
                  {items.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => onPickNotification(n)}
                      className={`w-full text-left px-3 py-2 border-b border-border last:border-0 hover:bg-muted/40 ${n.read ? "" : "bg-primary/5"}`}
                      data-testid={`notif-item-${n.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-xs mono uppercase">{n.type}</div>
                        <div className="text-[10px] text-muted-foreground mono whitespace-nowrap">
                          {new Date(n.created_at).toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      {n.description && (
                        <div className="text-[11px] text-muted-foreground truncate" style={{ maxWidth: 320 }}>
                          {typeof n.description === "string" ? n.description : JSON.stringify(n.description).slice(0, 80)}
                        </div>
                      )}
                      {!n.read && <Check size={10} className="text-primary" />}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Avatar / Menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="rounded-full overflow-hidden w-8 h-8 flex items-center justify-center bg-primary text-primary-foreground mono text-xs hover:ring-2 hover:ring-primary/40 transition"
            data-testid="topbar-avatar-btn"
            title={user?.name}
          >
            {user?.avatar_url
              ? <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
              : initial}
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-1 w-56 surface shadow-lg" data-testid="topbar-avatar-menu">
              <div className="p-3 border-b border-border">
                <div className="text-sm font-semibold truncate">{user?.name}</div>
                <div className="text-[10px] mono uppercase text-muted-foreground">{user?.role}</div>
                <div className="text-[10px] text-muted-foreground truncate">{user?.email}</div>
              </div>
              <button
                onClick={() => { setMenuOpen(false); setProfileOpen(true); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/40 text-left"
                data-testid="topbar-profile-link"
              >
                <UserIcon size={14} /> YOUR PROFILE
              </button>
              <button
                onClick={() => { setMenuOpen(false); toggleTheme(); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/40 text-left"
                data-testid="topbar-theme-toggle"
              >
                {user?.theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
                {user?.theme === "dark" ? "LIGHT MODE" : "DARK MODE"}
              </button>
              {/* iter 44 — Settings + Team links removed from the avatar
                  mini-menu. Settings is now reached exclusively via the gear
                  icon in the main TopBar row. Team lives inside /settings. */}
              <div className="border-t border-border" />
              <button
                onClick={() => { setMenuOpen(false); logout(); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-destructive/10 text-destructive text-left"
                data-testid="topbar-logout"
              >
                <SignOut size={14} /> SIGN OUT
              </button>
            </div>
          )}
        </div>
      </div>
      <UserProfileDrawer open={profileOpen} onClose={() => setProfileOpen(false)} />
    </header>
  );
}


/* ------------------------------------------------------------------------ *
 * iter 39 — Recently-viewed jobs menu.
 *
 * Pulls from /users/me/recent-jobs (server-persisted). Renders a clock icon
 * in the TopBar; clicking it pops a mini-panel of {job_number, subject_name}
 * rows. Each row links to /job-dash/:id.
 * ------------------------------------------------------------------------ */
function RecentJobsMenu() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);

  // Lazy load only when the panel opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    queueMicrotask(() => setLoading(true));
    api.get("/users/me/recent-jobs")
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        queueMicrotask(() => { setItems(list); setLoading(false); });
      })
      .catch(() => {
        if (cancelled) return;
        queueMicrotask(() => { setItems([]); setLoading(false); });
      });
    return () => { cancelled = true; };
  }, [open]);

  // Click outside closes.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const go = (job_id) => {
    setOpen(false);
    navigate(`/job-dash/${job_id}`);
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="btn btn-ghost btn-sm"
        data-testid="topbar-recent-jobs-btn"
        aria-label="Recently viewed jobs"
        title="Recently viewed jobs"
      >
        <ClockCounterClockwise size={16} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-80 surface shadow-lg max-h-[60vh] overflow-y-auto"
          data-testid="topbar-recent-jobs-panel"
        >
          <div className="flex items-center justify-between p-3 border-b border-border sticky top-0 bg-card">
            <span className="label-xs">Recently viewed</span>
            <button onClick={() => setOpen(false)} className="btn btn-ghost btn-sm" aria-label="Close">
              <XIcon size={12} />
            </button>
          </div>
          {loading && <div className="p-4 text-xs text-muted-foreground">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="p-4 text-xs text-muted-foreground" data-testid="recent-jobs-empty">
              No recent jobs yet. Open a job and it will show up here.
            </div>
          )}
          {!loading && items.map((j) => (
            <button
              key={j.job_id}
              onClick={() => go(j.job_id)}
              className="w-full text-left px-3 py-2 hover:bg-muted/40 border-b border-border last:border-0"
              data-testid={`recent-job-row-${j.job_id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="mono text-sm font-medium">{j.job_number || j.job_id.slice(0, 8)}</span>
                <span className="text-xs text-muted-foreground truncate flex-1 text-right">
                  {j.subject_name || "—"}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ *
 * iter 39 — Keyboard shortcuts cheatsheet menu (moved into the TopBar).
 *
 * Doc-only — the actual handlers live in the global keydown listener in
 * `TopBar` above (⌘J / ⌘.) and `Sidebar` (⌘K). Pressing `?` outside an
 * input toggles the panel for keyboard-driven users.
 * ------------------------------------------------------------------------ */
const SHORTCUTS = [
  { keys: ["⌘K"],        action: "Open global search" },
  { keys: ["⌘J", "⌘."],  action: "Create a new job" },
  { keys: ["?"],          action: "Toggle this cheatsheet" },
  { keys: ["Esc"],        action: "Close any open drawer or modal" },
];

function ShortcutsMenu() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // `?` (no modifiers) toggles the panel; Esc closes it.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const editable = tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable;
      if (editable) return;
      if (e.key === "?" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="btn btn-ghost btn-sm"
        data-testid="topbar-shortcuts-btn"
        aria-label="Keyboard shortcuts"
        title="Keyboard shortcuts (?)"
      >
        <Question size={16} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-80 surface shadow-lg"
          data-testid="topbar-shortcuts-panel"
        >
          <div className="flex items-center justify-between p-3 border-b border-border">
            <span className="label-xs">Keyboard shortcuts</span>
            <button onClick={() => setOpen(false)} className="btn btn-ghost btn-sm" aria-label="Close">
              <XIcon size={12} />
            </button>
          </div>
          <ul className="p-3 space-y-2">
            {SHORTCUTS.map((s, i) => (
              <li key={i} className="flex items-center justify-between gap-3 text-xs">
                <span className="text-muted-foreground">{s.action}</span>
                <span className="flex items-center gap-1 flex-shrink-0">
                  {s.keys.map((k, j) => (
                    <span key={j} className="flex items-center gap-1">
                      {j > 0 && <span className="text-muted-foreground">or</span>}
                      <kbd className="mono text-[10px] border border-border bg-muted/40 rounded px-1.5 py-0.5">{k}</kbd>
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
          <div className="px-3 pb-3 text-[10px] text-muted-foreground">
            Tip: shortcuts ignore the keys while you&apos;re typing in a field.
          </div>
        </div>
      )}
    </div>
  );
}
